import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { ReactComponent as CommentIcon } from '@/assets/icons/comment.svg';
import { ReactComponent as StarIcon } from '@/assets/icons/full-star.svg';
import { ReactComponent as InfoIcon } from '@/assets/icons/info-icon.svg';
import Text from '@/components/Text';
import useToasts from '@/hooks/useToasts';
import { MediaResource } from '@/types/resources';
import { api } from '@/utils/api';
import { CoverImagesImageStatus } from '@/utils/enums';

import ImageComment from './ImageComment';
import ImageCommentModal from './ImageCommentModal';

const ActionsLeft = styled.div`
  display: flex;
  gap: 12px;
  margin-left: 12px;
  opacity: 0;
  transition: opacity ease-out 0.2s;
`;

const ActionButton = styled.button`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

const MainCoverButton = styled(ActionButton)<{ $active?: boolean }>`
  display: flex;
  color: ${({ theme }) => theme.colors.lightGray};
  transition: opacity ease-out 0.2s;
  opacity: 0;
  ${Text} {
    transition: color ease-out 0.2s;
  }
  ${({ $active }) =>
    $active &&
    css`
      color: ${({ theme }) => theme.colors.production};
      opacity: 1;
      ${Text} {
        color: ${({ theme }) => theme.colors.black};
      }
    `}
  &:disabled {
    cursor: not-allowed;
    &:hover {
      border-color: ${({ theme }) => theme.colors.grayscale};
    }
  }
`;

const Holder = styled.div<{ $isSelected?: boolean; $isSelectable?: boolean }>`
  user-select: none;
  position: relative;
  width: 100%;
  cursor: pointer;
  transition: all ease-out 0.2s;
  img {
    width: 100%;
  }
  &:hover {
    ${ActionsLeft}, ${MainCoverButton} {
      opacity: 1;
    }
    &:before {
      opacity: 1;
    }
  }
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.2);
    opacity: 0;
    z-index: 0;
    transition: opacity ease-out 0.2s;
  }
  &::after {
    content: '';
    position: absolute;
    width: calc(100% + 8px);
    height: calc(100% + 8px);
    border-radius: 2px;
    border: solid 3px transparent;
    left: -4px;
    top: -4px;
    transition: border-color ease-out 0.2s;
  }
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      &::after {
        border-color: #2558ca;
      }
    `}
  ${({ $isSelectable }) =>
    $isSelectable &&
    css`
      &::after {
        border-color: #2558ca;
        border-style: dashed;
      }
    `}
`;

const Actions = styled.div<{ $isPreview?: boolean }>`
  position: absolute;
  left: 16px;
  bottom: 16px;
  width: calc(100% - 32px);
  display: flex;
  align-items: center;
  z-index: 1;
  ${({ $isPreview }) =>
    $isPreview &&
    css`
      left: 10px;
      bottom: 10px;
    `}
`;

const Img = styled.img`
  transition: all ease-out 0.2s;
  display: block;
`;

const IsOriginalBadge = styled.div<{ $isPreview?: boolean }>`
  display: flex;
  align-items: center;
  height: 28px;
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: ${({ theme }) => theme.colors.white};
  padding: 0 12px;
  border-radius: 100px;
  gap: 6px;
  ${({ $isPreview }) =>
    $isPreview &&
    css`
      top: 8px;
      right: 8px;
      padding: 0 8px;
      height: 22px;
    `}
`;

interface Props {
  status: CoverImagesImageStatus;
  image: MediaResource<
    {
      preview?: string;
    },
    {
      status: CoverImagesImageStatus;
      isMain?: boolean;
      comment: string;
      keepOriginal?: boolean;
    }
  >;
  selectedImages: number[];
  isPreviewMode: boolean;
  selectableImages: number[];
  sentImagesCount?: number;
  setSelectedImages: React.Dispatch<React.SetStateAction<number[]>>;
  onLightboxClick: () => void;
}

const Image: FC<Props> = ({
  status,
  image,
  selectedImages,
  isPreviewMode,
  selectableImages,
  sentImagesCount,
  setSelectedImages,
  onLightboxClick,
}) => {
  const [showCommentModal, setShowCommentModal] = useState(false);

  const { id } = useParams();

  const queryClient = useQueryClient();

  const toasts = useToasts();

  const onClick = () => {
    setSelectedImages((prev) => {
      if (!prev.includes(image.id)) {
        return [...prev, image.id];
      }
      return prev.filter((element) => element !== image.id);
    });
  };

  const isSelected = selectedImages.includes(image.id);

  const isSelectable = selectableImages.includes(image.id);

  const onToggleMainImage = async () => {
    const toastId = toasts.loading();

    try {
      await api.post(`/v1/cover-images/toggle-main-cover-image`, {
        image_id: image.id,
        model_id: id,
      });

      await queryClient.invalidateQueries(['cover_images_project', { id }], {
        exact: true,
      });

      toasts.success(toastId);
    } catch {
      toasts.error(toastId);
    }
  };

  return (
    <div>
      <Holder
        onClick={onClick}
        $isSelected={isSelected}
        $isSelectable={isSelectable}
      >
        <Img
          src={image.versions.preview ?? image.versions.original}
          alt="generated"
          loading="lazy"
          id={'image-' + image.id}
        />
        {status === CoverImagesImageStatus.PENDING &&
          !!image.meta?.keepOriginal && (
            <IsOriginalBadge $isPreview={isPreviewMode}>
              {!isPreviewMode && <InfoIcon />}
              <Text $type={isPreviewMode ? 'extraSmall' : 'smallHeadline'}>
                Moved to Fix
              </Text>
            </IsOriginalBadge>
          )}
        <Actions $isPreview={isPreviewMode}>
          <MainCoverButton
            disabled={
              image.meta?.status === CoverImagesImageStatus.SENT ||
              !!sentImagesCount
            }
            $active={image.meta?.isMain}
            onClick={(e) => {
              e.stopPropagation();
              onToggleMainImage();
            }}
          >
            <StarIcon />
          </MainCoverButton>
          <ActionsLeft>
            <ActionButton
              onClick={(e) => {
                e.stopPropagation();
                onLightboxClick();
              }}
            >
              <svg
                width="17"
                height="18"
                viewBox="0 0 17 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.57888 14.5383C11.1335 14.5383 14.0152 11.6454 14.0152 8.07677C14.0152 4.50816 11.1335 1.61523 7.57888 1.61523C4.02421 1.61523 1.14258 4.50816 1.14258 8.07677C1.14258 11.6454 4.02421 14.5383 7.57888 14.5383Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.1309 12.646L15.8548 16.3845"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ActionButton>
            {!image.meta?.comment &&
              image.meta?.status !== CoverImagesImageStatus.FIXING &&
              image.meta?.status !== CoverImagesImageStatus.SENT && (
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowCommentModal(true);
                  }}
                >
                  <CommentIcon />
                </ActionButton>
              )}
          </ActionsLeft>
        </Actions>
      </Holder>
      {status === CoverImagesImageStatus.FIXING && image.meta?.comment && (
        <ImageComment comment={image.meta.comment} />
      )}
      <AnimatePresence>
        {showCommentModal && (
          <ImageCommentModal
            image={image}
            onClose={() => setShowCommentModal(false)}
            setSelectedImages={setSelectedImages}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Image;
